import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { reCachePromise } from '@medifind/hooks';
import { validateNPI, validateYoutubeVideoID } from '@medifind/interface';
import { MFLink } from '@medifind/shared-basic-components';
import { matchYoutubeID } from './matchYoutubeID';

export const isValidNPI = (value) => {
  if (!value) return false;
  const length = value.length;
  if (length !== 10 && length !== 15) return false;
  if (value.match(/[^0-9]/)) return false;
  const checkDigit = parseInt(value[length - 1], 10);
  const resultCheckDigit =
    (value
      .split('')
      .slice(0, length - 1)
      // Double the value of alternate digits, starting with the rightmost digit (excluding the check digit)
      .map((d, i) => parseInt(d, 10) * ((length - 2 - i) % 2 === 0 ? 2 : 1))
      // Take the sum of all the individual digits (i.e. 18 would be 1 + 8)
      .join('')
      .split('')
      .reduce((t, d) => t + parseInt(d, 10), 0) +
      // Add 24 to the value
      24) %
    // Take the units digit from the above sum
    10;
  // Subtract from 10 (Only subtract from 10 if the units digit isn't zero).
  if (resultCheckDigit === 0) return checkDigit === resultCheckDigit;
  else return 10 - resultCheckDigit === checkDigit;
};
export const addYupValidationsForPM = (yup) => {
  // Note async calls in yup are called on change for ANY fields in the form, wrapping is in reCache should always be done

  yup.addMethod(yup.BaseSchema, `nonCountable`, function () {
    this.spec.nonCountable = true;
    return this;
  });

  yup.addMethod(yup.string, `validateNPI`, function (message) {
    return this.test({
      name: 'validateNPI',
      message: message ? (
        message
      ) : (
        <>
          NPI cannot be claimed: If you believe this is an error, please{' '}
          <MFLink to="/contact" target="_blank">
            contact us
          </MFLink>
          . Currently, MediFind supports providers with an active US National Provider Identifier (NPI) and the following credentials: MD or
          MBBS, DO, DPM, NP, PA. MediFind does not currently support allied healthcare professionals in mental health, dentists, or
          chiropractors.
        </>
      ),
      test: async function (value) {
        if (!isValidNPI(value)) return this.createError({ message: 'NPI is not valid' });

        return reCachePromise(
          'yup-validateNPI',
          () =>
            validateNPI(value)
              .then(() => true)
              .catch(() => false),
          [value],
        );
      },
    });
  });

  yup.addMethod(yup.string, `isValidPhoneNumber`, function () {
    return this.test({
      name: 'isValidPhoneNumber',
      message: '${path} is not a valid phone number, international numbers should start with +country code',
      test: function (val) {
        if (val) {
          const valid = isPossiblePhoneNumber(val, 'US');
          if (!valid && val?.[0] === '+') return this.createError({ message: '${path} is not a valid phone number' });
          return valid;
        }
        return true;
      },
    });
  });

  yup.addMethod(yup.string, `hasValidYoutubeID`, function () {
    return this.test({
      name: 'hasValidYoutubeID',
      message: 'Not a valid YouTube video url',
      test: function (youtubeVideo, ctx) {
        const youtubeID = matchYoutubeID(youtubeVideo);
        if (ctx.schema.presence !== 'required' && !youtubeVideo) return true;
        if (youtubeID?.length === 11) {
          return reCachePromise(
            'yup-validateYoutubeVideoID',
            () =>
              validateYoutubeVideoID(youtubeID)
                .then(() => {
                  return true;
                })
                .catch((error) => {
                  return ctx.createError({ message: error?.message });
                }),
            [youtubeID],
          );
        } else {
          return false;
        }
      },
    });
  });
  const urlRegex = /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;

  yup.addMethod(yup.string, `uri`, function () {
    return this.test({
      name: 'hasValidYoutubeID',
      message: 'Not a valid url',
      test: function (url) {
        if (!url) return true;
        return !!url?.match(urlRegex);
      },
    });
  });

  yup.addMethod(yup.string, `hasValidName`, function (message) {
    return this.test({
      name: 'hasValidName',
      message: '${path} is not a valid name',
      test: function (val, { path }) {
        const isMiddleName = path.toLowerCase().includes('middle');
        const regex = isMiddleName ? /[0-9=+*\\/(){}|_":;,<>?~@#$\[\]%^\t]/g : /[0-9=+*\\/(){}|_":;,.<>?~@#$\[\]%^\t]/g; // when middle name '.' is allowed
        if (val) {
          const valid = !val?.match(regex);
          if (!valid) {
            const invalidChars = [...new Set(Array.from(val.matchAll(regex), (x) => "'" + x[0] + "'"))]; // take only unique characters
            return this.createError({
              message: message
                ? message
                : '${path} is not a valid name. ' +
                  `${invalidChars?.join(', ')} ${invalidChars.length > 1 ? 'are' : 'is'} not valid \
                character${invalidChars.length > 1 ? 's' : ''}`,
            });
          }
          return valid;
        }
        return true;
      },
    });
  });
};
